import React, { useState, useEffect } from "react";
import "../styles/components/PatientBasicDetails.scss";
import { useFormik } from "formik";
import Gender from "./Gender";
import Datepicker from "./Datepicker";
import LeydenSelect from "./LeydenSelect";
import { BasicDetailsSchema } from "../schemas/Patient.schema";
import TextInput from "./TextInput";
import { ID_OPTIONS, MAX_LENGTHS } from "../constants/options";
import { Button } from "react-bootstrap";
import Utility from "../utility/Utility";
import { Trans } from "react-i18next";
import ValidationError from "./ValidationError";
import { isMobile } from "react-device-detect";
import moment from "moment";
import ErrorBoundaryHandler from "./ErrorBoundaryHandler";
import { useAppSelector } from "../../src/redux/hooks";
import {
  PersistState
} from "../../src/redux/reducers/PersistSlice";
import { UserConfigState } from "../redux/reducers/UserConfigSlice";

const PatientBasicDetails = (props: any) => {
  const defaultDate = props?.defaultValues?.birthDate
    ? new Date(
        moment(props?.defaultValues?.birthDate, "YYYY-MM-DD").format(
          "MM-DD-YYYY"
        )
      )
    : null;
  const [idType, setIdType] = useState(props.defaultValues?.id || "");
  const [validateOnChangeFlag, setvalidateOnChangeFlag] = useState(false);
  const [idTypeMax, setIdTypeMax] = useState<number>(14);
  const [startDate, setStartDate] = useState<any>(defaultDate);
  const [selection, setSelection] = useState("");
  const [age, setAge] = useState<number>(0);
  const userState = Utility.getStateOfUser();
  const userPersistData = useAppSelector(PersistState);
  const userCountry = userPersistData.country;
  const userConfigDetails: any = useAppSelector(UserConfigState);

  useEffect(() => {
    if (props.defaultValues?.birthDate !== "") {
      setStartDate(
        props?.defaultValues?.birthDate
          ? new Date(
              moment(props?.defaultValues?.birthDate, "YYYY-MM-DD").format(
                "MM-DD-YYYY"
              )
            )
          : "" // this effect can be removed if not found useful, since in line 19 we are setting the default date obtained from the props
      );
    }
    if (props.defaultValues?.id !== "" 
      && props.defaultValues?.id !== undefined 
      && props.defaultValues?.idNumber !== "" 
    ) {
      setIdTypeMax(MAX_LENGTHS[props.defaultValues?.id] || 14);
    }
    
  }, []);

  useEffect(() => {
    const scrollCalendarIntoView = (e: any) => {
      if (
        e?.target?.id === "calendar-sprite" ||
        e?.target?.className ===
          "c-els-date-picker-container__calendar-btn c-els-link c-els-link--image u-els-anchorize" ||
        e?.target?.localName === "path" ||
        e?.target?.id === "calendar"
      ) {
        // click event only triggers when the calendar icon is clicked
        (
          document?.querySelector(
            "[data-testid='els-date-picker-input']"
          ) as HTMLDivElement
        )?.getBoundingClientRect().top +
          345 > //300(Datepicker calendar div height) + 45(height of the datepicker's input box)
        window.innerHeight
          ? window.scrollBy(
              0,
              (
                document?.querySelector(
                  "[data-testid='els-date-picker-input']"
                ) as HTMLDivElement
              )?.getBoundingClientRect()?.top +
                375 -
                window.innerHeight
            )
          : null; //scrolling to bring the datepicker calendar in view by 30px more than the above calculated value for safety
      }
    };

    document.addEventListener("click", e => scrollCalendarIntoView(e));

    return document.removeEventListener("click", e =>
      scrollCalendarIntoView(e)
    );
  }, []);
  const idOptions = Utility.getIdTypesForState(userState) || userConfigDetails?.countryIdentifierOptions;
  const formik = useFormik({
    initialValues: {
      gender: props.defaultValues?.gender || "",
      firstName: props.defaultValues?.firstName || "",
      middleName: props.defaultValues?.middleName || "",
      lastName: props.defaultValues?.lastName || "",
      birthDate: props.defaultValues?.birthDate || "",
      idNumber: props.defaultValues?.idNumber || "",
      mobileNumber: props.defaultValues?.mobileNumber || "",
      id: props.defaultValues?.id || "",
      age: props.defaultValues?.age || "",
      selectedOption: props.defaultValues?.selectedOption || "",
    },
    validateOnChange: validateOnChangeFlag,
    onSubmit: values => {
      Object.entries(values).map(val => {
        sessionStorage.setItem(val[0], val[1]);
      });
      props.onBasicDetails(values);
    },
    validationSchema: BasicDetailsSchema,
  });

  const handleSelectID = (option: any) => {
    formik.setFieldValue("idNumber", "");
    formik.setFieldValue("id", option.value);
    setIdTypeMax(MAX_LENGTHS[option.value] || 12);
    setIdType(option.value);
  };

  const handleDate = (e: any) => {
    setStartDate(new Date(moment(e, "DD/MM/YYYY").format("MM-DD-YYYY")));
    formik.setFieldValue(
      "birthDate",
      moment(e, "DD/MM/YYYY").format("YYYY-MM-DD") === "Invalid date"
        ? ""
        : moment(e, "DD/MM/YYYY").format("YYYY-MM-DD")
    );
  };

  const handleAge = (e: any) => {
    const ageInYears = e.target.value;
    if (ageInYears) {
      if (ageInYears > 0 && ageInYears <= 100) {
        // setStartDate(null);
        const dob = Utility.ageToDate(ageInYears); //yyyy-mm-dd
        setStartDate(new Date(moment(dob, "YYYY-MM-DD").format("MM-DD-YYYY")));
        formik.setFieldValue("birthDate", dob);
        formik.setFieldValue("age", ageInYears);
        setAge(ageInYears);
      } else {
        formik.setFieldValue("age", ageInYears);
        setAge(0);
      }
    } else {
      setStartDate(null);
      formik.setFieldValue("birthDate", "");
      formik.setFieldValue("age", "");
      setAge(0);
    }
  };

  const handleSubmit = (e: any) => {
    if (document.querySelector(".c-els-field__message--error")) {
      // restricts submitting the form if the leyden datepicker throws validation error
      e.preventDefault();
      return;
    } else {
      e.preventDefault();
      setvalidateOnChangeFlag(true);
      formik.handleSubmit(e);
    }
  };

  function handleSelectDate(e: any) {
    formik.setFieldValue("selectedOption", e.target.value);
    setSelection(e.target.value);
    props.setDob(false);
    props.setAge(true);
  }

  function handleSelectAge(e: any) {
    formik.setFieldValue("selectedOption", e.target.value);
    formik.setFieldValue("age", "");
    setAge(0);
    setSelection(e.target.value);
    props.setAge(false);
    props.setDob(true);
  }

  return (
    <>
      <form
        className="p-0 patientBasicDetails"
        onSubmit={e => {
          handleSubmit(e);
        }}
      >
        <div className="bg-light-gray ">
          <h5 className="bg-light-blue p-2 ps-3 heading">
            <Trans i18nKey={"basic_details"}></Trans>
          </h5>
          <div className="p-3 form-width">
            <Gender
              onChange={formik.handleChange}
              error={formik.errors.gender}
              defaultSelected={formik.values.gender}
              mandate={true}
            />
            <div className="mt-3">
              <TextInput
                label="First Name"
                label_key="first_name"
                type="text"
                id="firstName"
                maxLength={50}
                defaultValue={formik.values.firstName}
                error={formik.errors.firstName}
                onChange={formik.handleChange}
                mandate={true}
              />
            </div>
            <div className="mt-3">
              <TextInput
                label="Middle Name"
                label_key="middle_name"
                type="text"
                id="middleName"
                maxLength={50}
                defaultValue={formik.values.middleName}
                error={formik.errors.middleName}
                onChange={formik.handleChange}
                mandate={false}
              />
            </div>
            <div className="mt-4">
              <TextInput
                label="Last Name"
                label_key="last_name"
                type="text"
                id="lastName"
                maxLength={50}
                defaultValue={formik.values.lastName}
                error={formik.errors.lastName}
                onChange={formik.handleChange}
                mandate={true}
              />
            </div>

            <label className="label-1 mt-4">
              <>
                <Trans i18nKey={"birth_details"}></Trans>{" "}
                <span className="mandetory">*</span>
              </>
            </label>
            <div className="ms-1 mt-2">
              <input
                type="radio"
                id="dob"
                value="dob"
                name="birthDate"
                defaultChecked={props.dob === false}
                onChange={handleSelectDate}
              />
              <label
                htmlFor="dob"
                className={
                  selection === "dob"
                    ? "radioBtn-selected"
                    : "radioBtn-not-selected"
                }
              >
                <Trans i18nKey={"birth_date"}></Trans>
              </label>

              <input
                className="ml-2"
                type="radio"
                id="age"
                name="birthDate"
                value="age"
                defaultChecked={props?.age === false}
                onChange={handleSelectAge}
              />
              <label
                htmlFor="age"
                className={
                  selection === "age"
                    ? "radioBtn-selected"
                    : "radioBtn-not-selected"
                }
              >
                <Trans i18nKey={"age"}></Trans>
              </label>

              {!selection && (
                <ValidationError
                  error_key={formik.errors?.selectedOption?.toString() || ""}
                />
              )}
            </div>

            {(selection === "dob" ||
              (props.dob === false && selection !== "age")) && (
              <div className="align-items-center">
                {/* <div className="mt-3"> */}
                <Datepicker
                  value={startDate}
                  minDt={new Date(new Date().getFullYear() - 100, 1, 1)} // passes 100 years less than the current year to the Datepicker component
                  onChange={handleDate}
                  testId={"dp-newpatient"}
                  className="beneficiary-dp"
                />
                {/* </div> */}

                {isMobile && (
                  <ValidationError
                    error_key={formik.errors?.birthDate?.toString() || ""}
                  />
                )}

                {(selection === "dob" ||
                  (props.dob === false && selection !== "age")) &&
                  formik.values.birthDate !== "" && (
                    <div>
                      <div
                        className={
                          isMobile
                            ? "displayAgeLabel mt-4"
                            : "displayAgeLabel mt-3"
                        }
                      >
                        <Trans i18nKey={"age"}></Trans>
                      </div>
                      <div
                        className={isMobile ? "displayAge mt-2" : "displayAge"}
                      >
                        {Utility.getAge(formik.values?.birthDate)}{" "}
                      </div>
                    </div>
                  )}
                {!isMobile && (
                  <ValidationError
                    error_key={formik.errors?.birthDate?.toString() || ""}
                  />
                )}
              </div>
            )}

            {(selection === "age" ||
              (props.age === false && selection !== "dob")) && (
              <div className="align-items-center mt-3">
                <TextInput
                  label_key="age_in_years"
                  type="number"
                  id="age"
                  defaultValue={formik.values.age}
                  maxLength={3}
                  onChange={handleAge}
                  // error={formik.errors.age}
                />
                <ValidationError
                  error_key={formik.errors?.age?.toString() || ""}
                />
                {(selection === "age" ||
                  (props.age === false && selection !== "dob")) &&
                  formik.values.birthDate !== "" &&
                  age > 0 &&
                  age <= 100 && (
                    <div>
                      <div className={"displayAgeLabel mt-3"}>
                        <Trans i18nKey={"birth_date"}></Trans>
                      </div>
                      <div
                        className={isMobile ? "displayAge mt-2" : "displayAge"}
                      >
                        {Utility.formatDate(formik.values?.birthDate)}{" "}
                      </div>
                    </div>
                  )}
                {/* {!isMobile && (
                  <ValidationError
                    error_key={formik.errors?.age?.toString() || ""}
                  />
                )} */
                /* this is commented out to prevent multiple error messages from getting displayed and the errors are handled in line 267 */}
              </div>
            )}

            <div className="mt-3">
              <LeydenSelect
                label={"Select ID type"}
                label_key="select_id_optional"
                options={Utility.getValidIdentifierOptions(idOptions, userCountry)}
                onChange={handleSelectID}
                id={"id"}
                defaultSelected={formik.values.id}
                error={formik.errors.id}
                blankOptionLabel={Utility.getTranslatedBlankOption(
                  "select_id_optional"
                )}
              />
            </div>
            {idType && (
              <div className="mt-4">
                <TextInput
                  label="ID Number"
                  label_key="id_number"
                  type="idNumber"
                  id="idNumber"
                  defaultValue={formik.values.idNumber}
                  value={formik.values.idNumber}
                  error={formik.errors.idNumber}
                  maxLength={idTypeMax}
                  onChange={formik.handleChange}
                  dynamicPlaceHolder={Utility.getPlaceholderForState(userState, userCountry)}
                />
              </div>
            )}

            <div className="mt-4">
              <TextInput
                label="Mobile Number"
                label_key="mobile_number"
                type="number"
                id="mobileNumber"
                defaultValue={formik.values.mobileNumber}
                maxLength={10}
                error={formik.errors.mobileNumber}
                onChange={formik.handleChange}
                mandate={true}
              />
            </div>
          </div>
        </div>
        <div className="text-lg-start text-md-end text-end mt-4">
          <Button
            data-testid="basicDetailsSubmitBtn"
            type="submit"
            className={"patientSubmitBtn addPatientBtn"}
          >
            <Trans i18nKey={"next"}></Trans>
          </Button>
        </div>
      </form>
    </>
  );
};

export default ErrorBoundaryHandler(PatientBasicDetails);
